import type { ScratchcardTeaserPayload } from '#api/shared/@types/generated/backend/graphql-schema-types'

import { QUERY_PRICES } from '~/constants/queryParams'

const ascending = (a: number, b: number) => a - b

const isNumber = (val: number) => !isNaN(val)

const toPrice = (acc: number[], teaser: ScratchcardTeaserPayload) => {
  const newPrice = teaser.price?.value
  if (!newPrice || acc.includes(newPrice)) return acc

  acc.push(newPrice)
  return acc
}

export const useScratchcardFilter = () => {
  const router = useRouter()

  const filters = useState<number[]>(() => [])
  const filteredTeasers = useState<ScratchcardTeaserPayload[]>(() => [])
  const prices = useState<number[]>(() => [])
  const teasers = useState<ScratchcardTeaserPayload[]>(() => [])

  const setUp = async (newTeasers: ScratchcardTeaserPayload[] = teasers.value) => {
    setTeasers(newTeasers)
    setPrices(newTeasers)
    updateFilters(prices.value)
    updateFilteredTeaser(newTeasers, filters.value)
    await updateQueryParams(filters.value)
  }

  const handleSetFilter = async (newPriceFilter: number) => {
    setFilter(newPriceFilter)
    updateFilteredTeaser(teasers.value, filters.value)
    await updateQueryParams(filters.value)
  }

  const setFilter = (price: number) => {
    const filterSet = new Set(filters.value)

    if (filterSet.has(price)) {
      filterSet.delete(price)
    } else {
      filterSet.add(price)
    }

    filters.value = Array.from(filterSet)
  }

  const setPrices = (teasers: ScratchcardTeaserPayload[]) => {
    prices.value = teasers.reduce(toPrice, []).sort(ascending)
  }

  const setTeasers = (newTeasers: ScratchcardTeaserPayload[]) => {
    teasers.value = newTeasers
  }

  const updateFilters = (prices: number[]) => {
    filters.value = filters.value.filter((filter) => prices.includes(filter))
  }

  const updateFilteredTeaser = (teasers: ScratchcardTeaserPayload[], filters: number[]) => {
    if (filters.length <= 0) {
      filteredTeasers.value = teasers
      return
    }

    filteredTeasers.value = teasers.filter(
      (teaser) => !!teaser.price && filters.find((price) => price === teaser.price?.value),
    )
  }

  const updateQueryParams = async (filters: number[]) => {
    if (!router.currentRoute.value.path.includes('/play-now/scratchcards')) return

    await router.replace({
      query: {
        ...router.currentRoute.value.query,
        [QUERY_PRICES]: filters.length >= 1 ? filters.sort(ascending) : undefined,
      },
    })
  }

  onServerPrefetch(() => {
    const queryFilters = router.currentRoute.value.query[QUERY_PRICES]
    if (!queryFilters) return

    const queryArray = Array.isArray(queryFilters) ? queryFilters : [queryFilters]
    filters.value = queryArray.map(Number).filter(isNumber)
  })

  onBeforeUnmount(async () => {
    await router.replace({
      query: {
        ...router.currentRoute.value.query,
        [QUERY_PRICES]: undefined,
      },
    })
  })

  return {
    activePrices: readonly(filters),
    filteredTeasers: readonly(filteredTeasers),
    prices: readonly(prices),
    setFilter: handleSetFilter,
    setUp,
  }
}
